import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiClientWrapper, createApiClientWrapper } from '@spike/api-client';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import { getFromStorage } from 'auth';

let abortController = new AbortController();

const sessionUuidKey = 'sessionUuid';

const getSession = (): string => {
    const sessionUuid = sessionStorage.getItem(sessionUuidKey);
    const windowName = window.name;

    if (sessionUuid && windowName.length > 0) {
        return sessionUuid;
    } else {
        const newSessionUuid = uuid();

        sessionStorage.setItem(sessionUuidKey, newSessionUuid);

        //To detect a new Tab created by duplication, because the sessionStorage
        //is copied but not the window.name.
        window.name = newSessionUuid;

        return newSessionUuid;
    }
};

export const sessionUuid = getSession();

const config: AxiosRequestConfig = {
    baseURL: `${process.env.REACT_APP_HOST_URL}${process.env.REACT_APP_API_BASE_URL}`,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Session-Uuid': sessionUuid
    },
    timeout: 60000,
    validateStatus: status => {
        // Only treat 2xx status codes as success
        return status >= 200 && status < 300;
    }
};

const ApiClient: AxiosInstance = axios.create(config);

// REQUEST INTERCEPTOR: Add Authorization header automatically (only for protected endpoints)
ApiClient.interceptors.request.use(
    config => {
        const auth = getFromStorage();

        // Only add token for protected endpoints (exclude public endpoints)
        const isPublicEndpoint =
            config.url &&
            (config.url.includes('/login_orgadmin') ||
                config.url.includes('/master_data') ||
                config.url.includes('/login') ||
                config.url.includes('/signup') ||
                config.url.includes('/reset_password'));

        if (auth && auth.token && !isPublicEndpoint) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${auth.token}`;
        }
        return config;
    },
    error => Promise.reject(error)
);

// RESPONSE INTERCEPTOR: Handle version check and 401s
ApiClient.interceptors.response.use(
    response => {
        // Version check DISABLED for organizations project to prevent auth loop
        // The version check was causing page reloads after successful login
        // If needed in the future, uncomment the code below:
        /*
        const headerVersion =
            response.headers[
                process.env.REACT_APP_VERSION_HEADER_NAME || 'version'
            ];

        if (
            headerVersion !== undefined &&
            headerVersion !== process.env.REACT_APP_VERSION
        ) {
            window.location.reload();
        }
        */

        return response;
    },
    error => {
        // Handle 401 Unauthorized - Token expired or invalid
        // BUT: Don't logout if this is a login request itself (wrong credentials)
        const isLoginRequest = error.config?.url?.includes('/login_orgadmin');

        if (error.response?.status === 401 && !isLoginRequest) {
            // Import logoutThunk dynamically to avoid circular dependency
            import('../actions/login/OrganizationsLoginActions').then(({ logoutThunk }) => {
                import('../store').then(({ default: store }) => {
                    store.dispatch(logoutThunk());
                });
            });
        }

        return Promise.reject(error);
    }
);

export const createWrapper = (state: RootState): ApiClientWrapper => {
    const authMarketplaceId = state.login.auth.marketplaceId!;
    const marketplace = state.marketplace.marketplace;

    return createApiClientWrapper(
        ApiClient,
        createTokenConfig(state.login.auth.token!),
        createMultipartTokenConfig(state.login.auth.token!),
        authMarketplaceId,
        state.login.auth.timeZone!,
        state.masterData.data,
        uuid,
        authMarketplaceId === marketplace?.id ? marketplace : undefined
    );
};

export const createTokenConfig = (token: string): AxiosRequestConfig => {
    if (token) {
        return {
            headers: {
                Authorization: `Bearer ${token}`
            },
            signal: abortController.signal
        };
    } else {
        return {};
    }
};

export const createMultipartTokenConfig = (token: string): AxiosRequestConfig => {
    const tokenConfig = createTokenConfig(token);
    return {
        ...tokenConfig,
        headers: {
            ...tokenConfig.headers,
            'Content-Type': 'multipart/mixed'
        },
        signal: abortController.signal
    };
};

export const cancelAllRequests = () => {
    abortController.abort();
    abortController = new AbortController();
};

export const isCancelled = (error: unknown): boolean => {
    return axios.isCancel(error);
};

export default ApiClient;
